<template>
  <v-container fluid class="pa-0">
    <div v-if="Info">
      <!-- ACTIVE EVENT -->
      <div v-if="Info.active">
        <!-- Header -->
        <v-sheet class="px-4 pt-6 pb-4" color="grey lighten-5">
          <v-row align="center" no-gutters>
            <v-col cols="12" class="text-center">
              <v-avatar v-if="businessImage" size="64" class="mb-3">
                <v-img :src="businessImage" alt="Logo" />
              </v-avatar>

              <div class="text-h6 font-weight-bold" v-if="businessName">
                {{ businessName }}
              </div>

              <div class="text-body-2" v-if="businessPhone">
                {{ businessPhone | USPhoneNumber }}
              </div>

              <div class="text-body-2 mt-1" v-if="businessAddressLine">
                {{ businessAddressLine }}
              </div>
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Content -->
        <v-container class="px-4 pt-4 pb-10">
          <div class="text-h5 font-weight-bold black--text">
            {{ Info.name }}
          </div>

          <v-card class="mx-auto rounded-xl mt-5" flat v-if="Info.imageUrl">
            <v-img contain :src="Info.imageUrl"></v-img>
          </v-card>

          <div class="text-body-2 grey--text mt-1" v-if="Info.createdAt">
            Created {{ Info.createdAt | moment("from", "now") }}
          </div>

          <v-row class="mt-4" dense>
            <!-- Left / Main -->
            <v-col cols="12" md="8">
              <v-card class="rounded-xl" outlined>
                <v-img
                  v-if="Info.imageUrl"
                  :src="Info.imageUrl"
                  aspect-ratio="16/9"
                  class="rounded-xl"
                />
                <v-card-text class="pt-4">
                  <div
                    v-if="Info.description"
                    class="event-description"
                    v-html="Info.description"
                  ></div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Right / Summary -->
            <v-col cols="12" md="4">
              <v-card class="rounded-xl" outlined>
                <v-card-text>
                  <div class="text-body-2 grey--text">Contribution</div>

                  <div class="d-flex align-end mt-1">
                    <div class="text-h4 font-weight-medium black--text">
                      {{ displayAmount }}
                    </div>
                    <div class="text-body-2 grey--text ml-2" v-if="isFree">
                      (no payment needed)
                    </div>
                  </div>

                  <div class="mt-4">
                    <v-progress-linear
                      :value="reservationProgress"
                      height="10"
                      rounded
                    />
                    <div class="d-flex justify-space-between mt-2 text-body-2">
                      <div>{{ Info.submitCount || 0 }} Reservations</div>
                      <div>{{ maxSeats }} Seats</div>
                    </div>
                  </div>

                  <!-- Desktop button -->
                  <v-btn
                    class="mt-5 rounded-lg d-none d-md-flex"
                    block
                    large
                    color="success"
                    @click="signUpNow"
                  >
                    {{ ctaText }}
                  </v-btn>
                </v-card-text>
              </v-card>

              <div class="text-caption grey--text mt-3 d-none d-md-block">
                Powered by taleem.app
                <span v-if="version">({{ version }})</span>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <!-- Sticky mobile CTA -->
        <v-sheet class="mobile-cta d-md-none" elevation="12" color="white">
          <div class="px-4 py-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <div>
                <div class="text-caption grey--text">Contribution</div>
                <div class="text-subtitle-1 font-weight-bold black--text">
                  {{ displayAmount }}
                </div>
              </div>

              <div class="text-caption grey--text">
                {{ Info.submitCount || 0 }}/{{ maxSeats }}
              </div>
            </div>

            <v-btn
              block
              large
              color="success"
              class="rounded-lg"
              @click="signUpNow"
            >
              {{ ctaText }}
            </v-btn>

            <div class="text-caption grey--text text-center mt-2">
              Powered by taleem.app <span v-if="version">({{ version }})</span>
            </div>
          </div>
        </v-sheet>
      </div>

      <!-- EXPIRED -->
      <v-container v-else class="px-4 py-10">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="6" class="text-center">
            <lottie
              :options="defaultOptions"
              v-on:animCreated="handleAnimation"
            />
            <div class="text-h5 font-weight-bold mt-4">
              This link has expired
            </div>
            <div class="text-body-2 grey--text mt-2">
              Please contact customer support to receive a new URL
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-container>
</template>

<script>
import EventDataService from "@/modules/Events/service.js";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/pinjump.json";

const pjson = require("../../../../package.json");

export default {
  name: "event-info",
  components: { Lottie },

  data() {
    return {
      EventDataId: null,
      loading: false,
      defaultOptions: { animationData: animationData.default },
      notFound: false,
      Info: null,
      maxSeats: 25,
    };
  },

  created() {
    this.loadData();
  },

  computed: {
    // Normalize Business object (you had Info.Business in template, and Info.Info.Business in script)
    business() {
      return this.Info?.Business || this.Info?.Info?.Business || {};
    },

    businessName() {
      return this.business?.name || "";
    },

    businessPhone() {
      return this.business?.phoneNumber || "";
    },

    businessImage() {
      return this.business?.imageUrl || "";
    },

    businessAddressLine() {
      const a = this.business?.address;
      const c = this.business?.city;
      const s = this.business?.state;
      const z = this.business?.zip;

      // Build a nice single line, skip missing parts
      const parts = [];
      if (a) parts.push(a);
      const cityState = [c, s].filter(Boolean).join(", ");
      if (cityState) parts.push(cityState);
      if (z) parts.push(z);
      return parts.join(" ");
    },

    reservationProgress() {
      const count = Number(this.Info?.submitCount || 0);
      const max = Number(this.maxSeats || 25);
      if (!max) return 0;
      return Math.min((count / max) * 100, 100);
    },

    amountNumber() {
      // if amount comes as string, still handle it
      const n = Number(this.Info?.amount || 0);
      return Number.isFinite(n) ? n : 0;
    },

    isFree() {
      return this.amountNumber <= 0;
    },

    displayAmount() {
      // show "Free" instead of "$0.00"
      if (this.isFree) return "Free";
      // keep your existing currency filter look
      return this.$options.filters.currency
        ? this.$options.filters.currency(this.amountNumber)
        : `$${this.amountNumber.toFixed(2)}`;
    },

    ctaText() {
      return this.isFree ? "Reserve for Free" : "Reserve Now";
    },

    version() {
      return pjson.version;
    },
  },

  methods: {
    signUpNow() {
      this.$router.push({ path: `/e/${this.EventDataId}/apply` });
    },

    handleAnimation(anim) {
      this.anim = anim;
    },

    loadData() {
      const id = this.$route.params.id;

      return EventDataService.getPublicEventById(id)
        .then((response) => {
          if (response.data) {
            this.Info = response.data;
            this.EventDataId = this.Info.id;

            // Use normalized business name for title
            const title =
              this.Info?.Business?.name ||
              this.Info?.Info?.Business?.name ||
              this.Info?.name ||
              "Event";
            document.title = title;
          } else {
            this.notFound = true;
          }

          this.$Progress.finish();
        })
        .catch(() => {
          this.loading = false;
          this.$Progress.finish();
        });
    },
  },
};
</script>

<style scoped>
.event-description {
  line-height: 1.6;
  font-size: 14px;
}

/* Sticky mobile CTA */
.mobile-cta {
  position: sticky;
  bottom: 0;
  width: 100%;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>